'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import { type ReactNode, useState } from 'react';
import { useMyLook } from '@/lib/api/auth/hooks';
import { useOpenFuturesPositionsCount } from '@/lib/api/futures/hooks';
import { useOpenPositionsCount } from '@/lib/api/history/profile-hooks';
import { useRewardsCounters } from '@/lib/api/rewards-hub/hooks';
import { LEADERBOARD_PATH, PROMOCODES_HREF } from '@/lib/components/RewardsHub/routes';
import { ZoneLink } from '@/lib/components/ZoneLink';
import { FEATURE_FLAGS } from '@/lib/config/features';
import { DOCS_URL } from '@/lib/constants/docs';
import { useAuthSession, useSignOut } from '@/lib/hooks/useAuth';
import { useGooglePlayUrl } from '@/lib/hooks/useGooglePlayUrl';
import { useLanguageSelection } from '@/lib/hooks/useLanguageSelection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/lib/ui-kit/accordion';
import { AnimatedIcon } from '@/lib/ui-kit/animated-icon';
import { AnimatedLogo, WORDMARK_ON_DARK, WORDMARK_ON_LIGHT } from '@/lib/ui-kit/animated-logo';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/lib/ui-kit/dialog';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { buttonVariants } from '@/lib/ui-kit-v2/button';
import { LanguageRow } from '@/lib/ui-kit-v2/language-row';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { ThemeToggle } from '@/lib/ui-kit-v2/user-menu';
import { formatBadgeCount } from '@/lib/utils/formatBadgeCount';
import {
  NAV_SECTIONS,
  type NavItem,
  type NavItemAction,
  type NavItemBadge,
  type NavSection,
} from './navSections';
import { CARD_LOTTIE_PROPS, FIRE_LOTTIE_PROPS } from './navStyles';
import { CARD_PATH, LOGIN_PATH, REFERRALS_PATH, SIGNUP_PATH } from './routes';

// ── Account rows ──

/** `nav.*` labels of the account group. */
type AccountKey =
  | 'wallet'
  | 'promocodes'
  | 'leaderboard'
  | 'news'
  | 'api'
  | 'settings'
  | 'helpCenter';

interface AccountItem {
  key: AccountKey;
  href: string;
  /** Opens in a new tab. */
  external?: boolean;
  /** Figma marks API with a link icon instead of the arrow. */
  hasLinkIcon?: boolean;
}

const WALLET_ITEM: AccountItem = { key: 'wallet', href: '/account?tab=wallet' };
const PROMOCODES_ITEM: AccountItem = { key: 'promocodes', href: PROMOCODES_HREF };
const LEADERBOARD_ITEM: AccountItem = { key: 'leaderboard', href: LEADERBOARD_PATH };
const NEWS_ITEM: AccountItem = { key: 'news', href: '/news' };
const API_ITEM: AccountItem = {
  key: 'api',
  href: DOCS_URL.apiIntroduction,
  external: true,
  hasLinkIcon: true,
};
const SETTINGS_ITEM: AccountItem = { key: 'settings', href: '/account?tab=settings' };
const HELP_ITEM: AccountItem = { key: 'helpCenter', href: DOCS_URL.home, external: true };

// Figma 15071:148430 (signed in) / 15071:148542 (guest).
const USER_ACCOUNT_ITEMS: AccountItem[] = [
  WALLET_ITEM,
  ...(FEATURE_FLAGS.ENABLE_PROMOCODES ? [PROMOCODES_ITEM] : []),
  LEADERBOARD_ITEM,
  NEWS_ITEM,
  API_ITEM,
  SETTINGS_ITEM,
  HELP_ITEM,
];
const GUEST_ACCOUNT_ITEMS: AccountItem[] = [LEADERBOARD_ITEM, NEWS_ITEM, HELP_ITEM];

// ── Styles (Figma 15071:148430) ──

const ROW_TEXT_CLASS = 'text-[15px] font-medium leading-5 text-v2-text-primary';
const MENU_ROW_CLASS = cn('flex h-[52px] w-full cursor-pointer items-center gap-2', ROW_TEXT_CLASS);
const ACCOUNT_ROW_CLASS = cn(
  'flex h-12 w-full cursor-pointer items-center gap-1.5',
  ROW_TEXT_CLASS,
);
const SUB_ROW_CLASS =
  'flex h-11 w-full cursor-pointer items-center gap-2 text-left text-[14px] leading-[19px] text-v2-text-secondary transition-colors hover:text-v2-text-primary';
const ARROW_CLASS = 'ml-auto shrink-0 text-v2-text-secondary';
const DIVIDER_CLASS = 'h-px w-full shrink-0 bg-v2-border';
const WORDMARK_BY_THEME: Record<string, string> = {
  light: WORDMARK_ON_LIGHT,
  dark: WORDMARK_ON_DARK,
};

/** A guest doesn't see the rows that only make sense with an account. */
function visibleItems(section: NavSection, isGuest: boolean): NavItem[] {
  return isGuest ? section.items.filter((item) => !item.isAuthOnly) : section.items;
}

// ── Parts ──

interface BadgeProps {
  count: number;
}

function Badge({ count }: BadgeProps) {
  if (count <= 0) return null;
  return (
    <span
      translate="no"
      className="flex items-center rounded-[8px] bg-red-400 px-[5px] py-px text-[10px] font-medium leading-[14px] text-white"
    >
      {formatBadgeCount(count)}
    </span>
  );
}

interface SubRowProps {
  item: NavItem;
  isGuest: boolean;
  badges: Record<NavItemBadge, number>;
  onNavigate: () => void;
  onAction: (action: NavItemAction) => void;
}

function SubRow({ item, isGuest, badges, onNavigate, onAction }: SubRowProps) {
  const t = useTranslations('nav.menu');
  const label = t(`${item.key}.title`);
  const badge = item.badge ? <Badge count={badges[item.badge]} /> : null;
  const handleAction = () => {
    if (item.action) onAction(item.action);
  };

  if (isGuest && item.needsAccount) {
    return (
      <ZoneLink href={SIGNUP_PATH} onClick={onNavigate} className={SUB_ROW_CLASS}>
        {label}
      </ZoneLink>
    );
  }
  if (item.action) {
    return (
      <button type="button" onClick={handleAction} className={SUB_ROW_CLASS}>
        {label}
      </button>
    );
  }
  return (
    <ZoneLink href={item.href ?? '/'} onClick={onNavigate} className={SUB_ROW_CLASS}>
      {label}
      {badge}
    </ZoneLink>
  );
}

interface SectionTitleProps {
  section: NavSection;
}

function SectionTitle({ section }: SectionTitleProps) {
  const t = useTranslations('nav');
  if (section.key !== 'cryptoTrading') return t(section.key);
  return (
    <span className="flex items-center gap-2">
      {t('cryptoTrading')}
      <AnimatedIcon
        name="nav-fire"
        size={16}
        loop
        playOnHover={false}
        lottieProps={FIRE_LOTTIE_PROPS}
      />
    </span>
  );
}

interface AccountRowProps {
  item: AccountItem;
  onNavigate: () => void;
}

function AccountRow({ item, onNavigate }: AccountRowProps) {
  const t = useTranslations('nav');
  const icon = item.hasLinkIcon ? (
    <Icon name="Link2" size={14} className="shrink-0 text-v2-text-secondary" />
  ) : (
    <Icon name="ArrowSmallRight" size={20} className={ARROW_CLASS} />
  );
  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        className={ACCOUNT_ROW_CLASS}
      >
        {t(item.key)}
        {icon}
      </a>
    );
  }
  return (
    <ZoneLink href={item.href} onClick={onNavigate} className={ACCOUNT_ROW_CLASS}>
      {t(item.key)}
      {icon}
    </ZoneLink>
  );
}

interface ProfileRowProps {
  onNavigate: () => void;
}

/** The signed-in head of the drawer (Figma 15071:148441). */
function ProfileRow({ onNavigate }: ProfileRowProps) {
  const { user } = useAuthSession();
  const look = useMyLook();
  if (!user) return null;
  const name = user.name || user.email;

  return (
    <ZoneLink
      href={`/stats/profile/${user.id}`}
      onClick={onNavigate}
      className="flex w-full shrink-0 items-center gap-3 border-b border-solid border-v2-border py-3"
    >
      <UserAvatar
        userId={user.id}
        name={user.name}
        look={look}
        size={44}
        className="border border-solid border-v2-border dark:border-[#34373e]"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="truncate text-[15px] font-semibold leading-5 text-v2-text-primary">
          {name}
        </span>
        {user.name && user.email ? (
          <span className="truncate text-[12px] leading-4 text-v2-text-secondary">
            {user.email}
          </span>
        ) : null}
      </span>
      <Icon name="ArrowSmallRight" size={20} className={ARROW_CLASS} />
    </ZoneLink>
  );
}

/** The shared language accordion, at the drawer's row size. */
function DrawerLanguageRow() {
  const t = useTranslations('nav');
  const tLangToasts = useTranslations('toasts.language');
  const {
    selectedLocale,
    languageOptions,
    handleLanguageChange,
    isPending: isLocalePending,
  } = useLanguageSelection({
    redirectOnChange: true,
    showToast: true,
    toastMessages: {
      success: tLangToasts('updated'),
      error: tLangToasts('updateFailed'),
    },
  });

  return (
    <LanguageRow
      options={languageOptions}
      selectedCode={selectedLocale}
      onSelect={handleLanguageChange}
      label={t('language')}
      size="L"
      disabled={isLocalePending}
    />
  );
}

interface DrawerContentProps {
  isGuest: boolean;
  onClose: () => void;
  onDeposit?: () => void;
  onConvert?: () => void;
}

/** Mounted only while the drawer is open, so its counters are fetched on demand. */
function DrawerContent({ isGuest, onClose, onDeposit, onConvert }: DrawerContentProps) {
  const t = useTranslations('nav');
  const { user } = useAuthSession();
  const { resolvedTheme } = useTheme();
  const { count: openPositions } = useOpenPositionsCount(user?.id ?? '');
  const { count: openFutures } = useOpenFuturesPositionsCount();
  const { data: readyRewards = 0 } = useRewardsCounters(!isGuest);
  const signOut = useSignOut();
  const locale = useLocale();
  const googlePlayUrl = useGooglePlayUrl();

  const badges: Record<NavItemBadge, number> = { openPositions, openFutures, readyRewards };
  const accountItems = isGuest ? GUEST_ACCOUNT_ITEMS : USER_ACCOUNT_ITEMS;
  const wordmarkFill = WORDMARK_BY_THEME[resolvedTheme ?? 'dark'] ?? WORDMARK_ON_DARK;

  const handleAction = (action: NavItemAction) => {
    onClose();
    if (action === 'deposit') onDeposit?.();
    if (action === 'convert') onConvert?.();
  };

  // The drawer also opens in the trade and news zones: home is a full load.
  const handleLogout = () => {
    onClose();
    signOut.mutate();
    window.location.assign(`/${locale}`);
  };

  return (
    <>
      <DialogTitle className="sr-only">{t('userMenu')}</DialogTitle>
      <div className="flex h-[52px] shrink-0 items-center justify-between px-4 sm:px-6">
        <ZoneLink href="/" onClick={onClose} aria-label={t('outpollHome')} className="flex">
          <AnimatedLogo wordmarkFill={wordmarkFill} width={117} />
        </ZoneLink>
        <DialogClose
          aria-label={t('close')}
          className="flex size-10 cursor-pointer items-center justify-center text-v2-text-secondary transition-colors hover:text-v2-text-primary"
        >
          <Icon name="Cancel" size={24} />
        </DialogClose>
      </div>

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-4 py-2 sm:px-6',
          !isGuest && 'pb-10',
        )}
      >
        {isGuest ? null : <ProfileRow onNavigate={onClose} />}

        <div className="flex flex-col">
          <Accordion type="single" collapsible>
            {NAV_SECTIONS.map((section) => (
              <AccordionItem key={section.key} value={section.key} className="border-b-0">
                <AccordionTrigger
                  className={cn(MENU_ROW_CLASS, 'rounded-none py-0 hover:no-underline')}
                  icon={
                    <Icon
                      name="ArrowSmallDown2"
                      size={20}
                      className={cn(ARROW_CLASS, 'transition-transform duration-200')}
                    />
                  }
                >
                  <SectionTitle section={section} />
                </AccordionTrigger>
                <AccordionContent className="flex flex-col pb-0 pl-3">
                  {visibleItems(section, isGuest).map((item) => (
                    <SubRow
                      key={item.key}
                      item={item}
                      isGuest={isGuest}
                      badges={badges}
                      onNavigate={onClose}
                      onAction={handleAction}
                    />
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {/* /account sends a guest home, so a guest's Referral is sign-up. */}
          <ZoneLink
            href={isGuest ? SIGNUP_PATH : REFERRALS_PATH}
            onClick={onClose}
            className={MENU_ROW_CLASS}
          >
            {t('referral')}
          </ZoneLink>
          {FEATURE_FLAGS.ENABLE_CARD_NAV ? (
            <ZoneLink href={CARD_PATH} onClick={onClose} className={MENU_ROW_CLASS}>
              {t('card')}
              <AnimatedIcon
                name="nav-card-flip"
                size={20}
                loop
                playOnHover={false}
                lottieProps={CARD_LOTTIE_PROPS}
              />
            </ZoneLink>
          ) : null}
        </div>

        <div className={DIVIDER_CLASS} />
        <div className="flex flex-col">
          {accountItems.map((item) => (
            <AccountRow key={item.key} item={item} onNavigate={onClose} />
          ))}
        </div>

        <div className={DIVIDER_CLASS} />
        <div className="flex flex-col">
          <div className={ACCOUNT_ROW_CLASS}>
            {t('theme')}
            <span className="ml-auto">
              <ThemeToggle size="L" />
            </span>
          </div>
          <DrawerLanguageRow />
          <a
            href={googlePlayUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className={ACCOUNT_ROW_CLASS}
          >
            {t('downloadApp')}
            <Icon name="ArrowSmallRight" size={20} className={ARROW_CLASS} />
          </a>
        </div>

        {isGuest ? null : (
          <>
            <div className={DIVIDER_CLASS} />
            <button type="button" onClick={handleLogout} className={ACCOUNT_ROW_CLASS}>
              {t('logout')}
            </button>
          </>
        )}
      </div>

      {isGuest ? <GuestFooter onNavigate={onClose} /> : null}
    </>
  );
}

interface GuestFooterProps {
  onNavigate: () => void;
}

/** Figma 15071:148542: sign-up first, both full width, pinned under the scroll. */
function GuestFooter({ onNavigate }: GuestFooterProps) {
  const t = useTranslations('nav');

  // /auth is its own zone: plain links, styled as the buttons.
  return (
    <div className="flex shrink-0 flex-col gap-2 border-t border-solid border-v2-border px-4 pt-4 pb-11 sm:px-6">
      <ZoneLink
        href={SIGNUP_PATH}
        onClick={onNavigate}
        className={cn(buttonVariants({ appearance: 'accent', size: 'L' }), 'w-full')}
      >
        {t('signup')}
      </ZoneLink>
      <ZoneLink
        href={LOGIN_PATH}
        onClick={onNavigate}
        className={cn(
          buttonVariants({ appearance: 'outline', size: 'L' }),
          'w-full border-v2-border',
        )}
      >
        {t('login')}
      </ZoneLink>
    </div>
  );
}

interface NavMobileDialogProps {
  children: ReactNode;
  isGuest: boolean;
  onDeposit?: () => void;
  onConvert?: () => void;
}

/** The burger drawer below 1024px (Figma 15071:148232–148661): sections as accordions. */
export const NavMobileDialog = ({
  children,
  isGuest = true,
  onDeposit,
  onConvert,
}: NavMobileDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleClose = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className="fixed inset-0 left-0 top-0 z-[9999] flex h-dvh !max-h-none !w-screen !max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 !bg-v2-surface-primary p-0"
      >
        <DrawerContent
          isGuest={isGuest}
          onClose={handleClose}
          onDeposit={onDeposit}
          onConvert={onConvert}
        />
      </DialogContent>
    </Dialog>
  );
};
